<template>
  <section class="h-full mx-4" id="salesDocumentsView">
    <BaseTable
      :headers="transactionsHeaders"
      :list="transactions"
      :total-records="transactionsTotalCount"
      table-id="representatives-transactions"
      hide-multiselect-delete
      is-add-new-hidden
      clickable
      @refresh-data="onRefreshData"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters"
      @table-item-clicked="onItemClick">
      <template #custom="{ item }: any">
        <div class="text-center">
          <span
            :class="getStatusClasses(item)"
            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium">
            {{ getStatusText(item) }}
          </span>
        </div>
      </template>
      <template #filters>
        <div class="grid grid-cols-3 mx-10 my-5 justify-items-center">
          <!-- Col 1 -->
          <div class="space-y-4">
            <div>
              <BaseTriStateToggle
                :label="$t('representative.isInvalid')"
                name="isInactiveFilter"
                v-model="filterIsInvalid" />
            </div>
            <div>
              <BaseTriStateToggle
                :label="$t('representative.toApprove')"
                name="toApproveFilter"
                v-model="filterToApprove" />
            </div>
            <div>
              <BaseTriStateToggle
                :label="$t('baseObject.isActive')"
                name="isActive"
                v-model="filterIsActive" />
            </div>
            <div>
              <BaseTriStateToggle
                :label="$t('representative.toExport')"
                name="toExport"
                v-model="filterExported" />
            </div>
          </div>
          <!-- Col 2 -->
          <div class="space-y-1">
            <label class="text-sm tracking-wide text-gray-400 uppercase">{{
              $t('representative.representative')
            }}</label>
            <BaseMultiSelectDropdown
              class="w-64 text-sm"
              tags-outside
              :place-holder-text="$t('representative.selectRepresentative')"
              :list-items="dropdownRepresentatives"
              v-model="filterRepresentativesIds" />
          </div>
          <!-- Col 3 -->
          <div class="space-y-4">
            <div class="space-y-1">
              <label class="mb-1 text-sm tracking-wide text-gray-400 uppercase">{{
                $t('salesDocuments.filters.dateFrom')
              }}</label>
              <DateTimePicker
                v-model="filterDateStringFrom"
                :show-time-picker="false"
                startEmpty
                show-error-as-tooltip
                @validation-change="onDateValidationChange($event, 'filterDateStringFrom')" />
            </div>
            <div class="space-y-1">
              <label class="text-sm tracking-wide text-gray-400 uppercase">{{
                $t('salesDocuments.filters.dateTo')
              }}</label>
              <DateTimePicker
                v-model="filterDateStringTo"
                :show-time-picker="false"
                start-empty
                show-error-as-tooltip
                @validation-change="onDateValidationChange($event, 'filterDateStringTo')" />
            </div>
          </div>
        </div>
      </template>
    </BaseTable>
  </section>
</template>
<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';
import { useI18n } from 'vue-i18n';
import { getDCDocumentHeadersWithConditions } from '@/services/api/dataCollection';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';
import BaseMultiSelectDropdown from '@/components/common/BaseMultiSelectDropdown.vue';
import type { DCDocumentHeader, TransactionsFiltersData } from '@/types/DataCollection';
import type { SelectDropdownItem } from '@/types/Base';
import { useRouter } from 'vue-router';
import DateTimePicker from '@/components/common/DateTimePicker.vue';
import { datepickerToServerDateFormat } from '@/utils/date';

const { getRepresentativesLookup } = useLookupsStore();
const { dropdownRepresentatives } = storeToRefs(useLookupsStore());
const transactions = ref<Partial<DCDocumentHeader>[]>([]);
const transactionsTotalCount = ref<number>();
const filterIsActive = ref<boolean | undefined>();
const filterExported = ref<boolean | undefined>();
const filterDateStringFrom = ref<string | undefined>();
const filterDateStringTo = ref<string | undefined>();

const { t: $t } = useI18n({ useScope: 'global' });

const transactionsHeaders = ref([
  { value: 'documentNumber', text: $t('transactions.documentNumber'), type: 'text', active: true },
  {
    value: 'representativeCustomerCode',
    text: $t('ranges.customerCode'),
    type: 'text',
    active: true,
  },
  {
    value: 'representativeCustomerName',
    text: $t('representative.customerName'),
    type: 'text',
    active: true,
  },
  {
    value: 'customerSiteCode',
    text: $t('transactions.customerSiteCode'),
    type: 'text',
    active: true,
  },
  {
    value: 'customerSiteName',
    text: $t('transactions.customerSiteName'),
    type: 'text',
    active: true,
  },
  {
    value: 'representativeRecipientCode',
    text: $t('transactions.representativeRecipientCode'),
    type: 'text',
    active: true,
  },
  {
    value: 'representativeRecipientName',
    text: $t('transactions.representativeRecipientName'),
    type: 'text',
    active: true,
  },
  {
    value: 'officialDate',
    text: $t('transactions.officialDate'),
    type: 'text',
    active: true,
  },
  {
    value: 'isInvalid',
    text: $t('representative.status'),
    type: 'custom',
    active: true,
  },
]);

onMounted(async () => {
  await getRepresentativesLookup();
  await loadData({
    itemsPerPage: 50,
    sortColumn: '',
    sortAscending: true,
    page: 1,
  });
});

async function loadData(data: TransactionsFiltersData) {
  const res = await getDCDocumentHeadersWithConditions(data);

  if (res) {
    transactions.value = res.data;
    transactionsTotalCount.value = res.count;
  }
}

async function onRefreshData(data: TransactionsFiltersData) {
  const sortColumn = data.column !== undefined ? transactionsHeaders.value[data.column]?.value : '';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params = {
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 50,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
    isInvalid: data.isInvalid,
    toApprove: data.toApprove,
    representativesIds: data.representativesIds,
    isActive: data.isActive,
    exported: data.exported,
    dateFrom: datepickerToServerDateFormat(data.dateFrom || ''),
    dateTo: datepickerToServerDateFormat(data.dateTo || ''),
  };

  const res = await getDCDocumentHeadersWithConditions(params);

  if (res) {
    transactions.value = res.data;
    transactionsTotalCount.value = res.count;
  }
}

const filterIsInvalid = ref<boolean | undefined>(undefined);
const filterToApprove = ref<boolean | undefined>(undefined);
const filterRepresentativesIds = ref<SelectDropdownItem[]>([]);
async function onApplyFilters(data: TransactionsFiltersData) {
  data.isInvalid = filterIsInvalid.value;
  data.toApprove = filterToApprove.value;
  data.isActive = filterIsActive.value;
  data.exported = filterExported.value;
  data.dateFrom = filterDateStringFrom.value;
  data.dateTo = filterDateStringTo.value;
  filterRepresentativesIds.value
    ? (data.representativesIds = filterRepresentativesIds.value
        .map((item) => item.value)
        .filter((value): value is string => typeof value === 'string'))
    : [];
  await onRefreshData(data);
}

async function onResetFilters() {
  filterIsInvalid.value = undefined;
  filterToApprove.value = undefined;
  filterRepresentativesIds.value = [];
  filterIsActive.value = undefined;
  filterExported.value = undefined;
  filterDateStringFrom.value = undefined;
  filterDateStringTo.value = undefined;
  const params = {
    itemsPerPage: 50,
    sortColumn: '',
    sortAscending: true,
    page: 1,
  };

  await onRefreshData(params);
}

const router = useRouter();
function onItemClick(item: DCDocumentHeader) {
  router.push({ name: 'representatives-transactions-details', params: { id: item.id } });
}

function getStatusClasses(item: DCDocumentHeader): string {
  return item.isInvalid ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800';
}

function getStatusText(item: DCDocumentHeader): string {
  return item.isInvalid ? $t('common.invalid') : $t('common.valid');
}

const dateValidationErrors = ref<Record<string, boolean>>({});
function onDateValidationChange(
  validation: {
    isValid: boolean;
    error: string;
  },
  dateField: 'filterDateStringFrom' | 'filterDateStringTo'
) {
  dateValidationErrors.value[dateField] = !validation.isValid;
}
</script>
